/**
 * Column mapping + value coercion for the bulk task importer.
 *
 * Kept as a pure `.ts` module (like `team/mapTeam.ts`) so it can be unit
 * tested — vitest does not collect `.tsx`.
 *
 * Disciplines are tenant-configured, so every entry point takes the live list
 * rather than closing over a hardcoded one: a sheet that says "delivery" must
 * map to the tenant's `delivery` when it exists, not get silently folded into
 * `ops` the way the old hardcoded alias table did.
 */
import type { Task } from '../../lib/api'
import type { ParsedTable } from '../client/intake/types'
import { matchEmployee, type MatchableEmployee } from '../team/matchEmployee'

export type FieldKey =
  | 'title'
  | 'description'
  | 'status'
  | 'priority'
  | 'discipline'
  | 'due'
  | 'client'
  | 'assignee'
  | 'estimateHrs'

export const FIELDS: { key: FieldKey; label: string; required?: boolean; aliases: string[] }[] = [
  { key: 'title', label: 'Title', required: true, aliases: ['title', 'task', 'name', 'summary', 'todo'] },
  { key: 'description', label: 'Details', aliases: ['details', 'detail', 'description', 'notes', 'note', 'body', 'brief'] },
  { key: 'status', label: 'Status', aliases: ['status', 'state', 'stage'] },
  { key: 'priority', label: 'Priority', aliases: ['priority', 'prio', 'importance'] },
  { key: 'discipline', label: 'Discipline', aliases: ['discipline', 'team', 'department', 'type', 'category'] },
  { key: 'due', label: 'Due', aliases: ['due', 'duedate', 'deadline', 'dueon', 'date'] },
  { key: 'client', label: 'Client', aliases: ['client', 'account', 'company', 'business', 'customer'] },
  { key: 'assignee', label: 'Assignee', aliases: ['assignee', 'assignedto', 'owner', 'responsible', 'who', 'assigned'] },
  { key: 'estimateHrs', label: 'Estimate (hrs)', aliases: ['estimate', 'estimatehrs', 'hours', 'hrs', 'est'] },
]

const STATUS = ['todo', 'in_progress', 'review', 'done', 'blocked']
const PRIORITY = ['low', 'med', 'high', 'urgent']

/** Synonyms people type in sheets, keyed by the discipline they suggest. */
const DISCIPLINE_ALIASES: Record<string, string[]> = {
  sales: ['bd', 'biz dev', 'business development', 'new business'],
  marketing: ['mktg', 'growth', 'demand gen'],
  ops: ['operations', 'admin', 'back office'],
  delivery: ['fulfilment', 'fulfillment', 'production'],
  engineering: ['eng', 'dev', 'development'],
  design: ['creative'],
}

const norm = (h: string) => h.toLowerCase().replace(/[\s_\-./]+/g, '').replace(/[^a-z0-9]/g, '')

export function autoMap(headers: string[]): Record<number, FieldKey | null> {
  const map: Record<number, FieldKey | null> = {}
  const used = new Set<FieldKey>()
  headers.forEach((h, i) => {
    const n = norm(h)
    map[i] = null
    if (!n) return
    for (const f of FIELDS) {
      if (used.has(f.key)) continue
      if (f.aliases.some((a) => n === a || n.includes(a) || a.includes(n))) {
        map[i] = f.key
        used.add(f.key)
        break
      }
    }
  })
  return map
}

export function coerceStatus(v: string): string | undefined {
  const s = v.toLowerCase().trim()
  if (!s) return undefined
  if (STATUS.includes(s)) return s
  if (['to do', 'todo', 'backlog', 'new', 'open'].includes(s)) return 'todo'
  if (['in progress', 'doing', 'wip', 'started'].includes(s)) return 'in_progress'
  if (['review', 'in review', 'qa'].includes(s)) return 'review'
  if (['done', 'complete', 'completed', 'closed'].includes(s)) return 'done'
  if (['blocked', 'on hold', 'waiting'].includes(s)) return 'blocked'
  return undefined
}

export function coercePriority(v: string): string | undefined {
  const s = v.toLowerCase().trim()
  if (!s) return undefined
  if (PRIORITY.includes(s)) return s
  if (['medium', 'normal', 'moderate'].includes(s)) return 'med'
  if (['critical', 'p0'].includes(s)) return 'urgent'
  return undefined
}

/**
 * Map a sheet cell onto one of the tenant's configured disciplines. An exact
 * match always wins; a synonym only resolves when the discipline it points at
 * is actually configured. Anything else is reported rather than guessed.
 */
export function coerceDiscipline(v: string, configured: string[]): string | undefined {
  const s = v.toLowerCase().trim()
  if (!s) return undefined

  const allowed = configured.map((d) => d.toLowerCase().trim())
  if (allowed.includes(s)) return s

  for (const target of allowed) {
    if (DISCIPLINE_ALIASES[target]?.includes(s)) return target
  }
  return undefined
}

export function coerceDue(v: string): string | undefined {
  const s = v.trim()
  if (!s) return undefined
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10)
  const d = new Date(s)
  if (!isNaN(d.getTime())) return d.toISOString().slice(0, 10)
  return undefined // unparseable → dropped (with a note)
}

export interface MappedTask {
  draft: Partial<Task>
  /**
   * Kept beside the draft rather than on it: people are their own resource
   * (`assignments`), never a scalar field on the task row. The import endpoint
   * writes the assignment rows itself.
   */
  assigneeIds: string[]
  errors: string[]
  sourceRow: number
}

export function mapRows(
  table: ParsedTable,
  colMap: Record<number, FieldKey | null>,
  clientByName: Map<string, string>,
  disciplines: string[],
  employees: MatchableEmployee[] = [],
): MappedTask[] {
  return table.rows.map((cells, ri) => {
    const draft: Partial<Task> = {}
    const assigneeIds: string[] = []
    const errors: string[] = []
    cells.forEach((cell, ci) => {
      const key = colMap[ci]
      const val = (cell ?? '').trim()
      if (!key || !val) return
      switch (key) {
        case 'title':
          draft.title = val
          break
        case 'description':
          draft.description = val
          break
        case 'status': {
          const s = coerceStatus(val)
          if (s) draft.status = s
          else errors.push(`Status "${val}" not recognized`)
          break
        }
        case 'priority': {
          const p = coercePriority(val)
          if (p) draft.priority = p
          else errors.push(`Priority "${val}" not recognized`)
          break
        }
        case 'discipline': {
          const d = coerceDiscipline(val, disciplines)
          if (d) draft.discipline = d
          else errors.push(`Discipline "${val}" is not one of: ${disciplines.join(', ')}`)
          break
        }
        case 'due': {
          const d = coerceDue(val)
          if (d) draft.due = d
          else errors.push(`Due "${val}" is not a date`)
          break
        }
        case 'estimateHrs': {
          const n = Number(val.replace(/[^\d.]/g, ''))
          if (Number.isFinite(n) && n > 0) draft.estimateHrs = Math.round(n)
          break
        }
        case 'client': {
          const id = clientByName.get(val.toLowerCase())
          if (id) draft.clientId = id
          else errors.push(`No client named "${val}"`)
          break
        }
        case 'assignee': {
          // One cell may name several people ("Ana, Ben"). Each name is matched
          // independently so one typo doesn't cost the whole row its assignees.
          for (const raw of val.split(/[,;/]|\band\b/)) {
            const nm = raw.trim()
            if (!nm) continue
            const emp = matchEmployee(nm, employees)
            if (emp) {
              if (!assigneeIds.includes(emp.id)) assigneeIds.push(emp.id)
            } else {
              errors.push(`No teammate named "${nm}"`)
            }
          }
          break
        }
      }
    })
    if (!draft.title) errors.push('Title is required')
    return { draft, assigneeIds, errors, sourceRow: ri + 2 }
  })
}
